import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, HardHat, Search } from 'lucide-react';
import { InstallerForm } from './InstallerForm';
import { DeleteInstallerDialog } from './DeleteInstallerDialog';
import { Manager } from '@/types';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { PARTNER_BADGE_CLASS, partnerFilterChips, partnerTypeLabel } from '@/lib/partnerTypes';

export interface Installer {
  id?: string;
  name: string; // Maps to company_name
  address: string; // Maps to street
  city: string;
  state: string;
  zip_code: string;
  county: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  user_id?: string | null;
  internal_pm_manager_id?: string | null;
  internal_pm_manager_name?: string | null;
  installer_pm_manager_id?: string | null;
  installer_pm_manager_name?: string | null;
  biz_dev_rep_id?: string | null;
  biz_dev_rep_name?: string | null;
  email: string | null;
  phone: string | null;
  cost_ppw: number | null;
  /** 'EPC' or 'INSTALLER'; rows from before EPCs read as INSTALLER. */
  partner_type?: 'EPC' | 'INSTALLER';
  /** EPC price per watt. Never read by the cost engine (see lib/partnerTypes.ts). */
  price_ppw?: number | null;
  /** People linked to this company (user_installer_partners). */
  people_count?: number;
}

interface InstallersTabProps {
  installers: Installer[];
  managers: Manager[];
  onAddInstaller: (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => Promise<string>;
  onUpdateInstaller: (installerData: Installer) => Promise<string>;
  onDeleteInstaller: (installerId: string) => Promise<void>;
  isLoading: boolean;
}

export function InstallersTab({
  installers = [],
  managers = [],
  onAddInstaller,
  onUpdateInstaller,
  onDeleteInstaller,
  isLoading,
}: InstallersTabProps) {
  const { toast } = useToast();
  const [isAddingInstaller, setIsAddingInstaller] = useState(false);
  const [editingInstaller, setEditingInstaller] = useState<Installer | null>(null);
  const [deletingInstaller, setDeletingInstaller] = useState<Installer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const chips = partnerFilterChips(installers);

  const handleAddInstaller = async (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const installerId = await onAddInstaller(installerData);
      setIsAddingInstaller(false);
      return installerId;
    } catch (error: any) {
      console.error('Error in handleAddInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleUpdateInstaller = async (installerData: Installer) => {
    try {
      if (!installerData.id) {
        throw new Error('Installer ID is required for updates');
      }
      const installerId = await onUpdateInstaller(installerData);
      setEditingInstaller(null);
      return installerId;
    } catch (error: any) {
      console.error('Error in handleUpdateInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteInstaller = async () => {
    if (!deletingInstaller?.id) return;
    try {
      await onDeleteInstaller(deletingInstaller.id);
      setDeletingInstaller(null);
    } catch (error: any) {
      console.error('Error in handleDeleteInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const q = searchQuery.toLowerCase();
  const filteredInstallers = installers
    .filter((p) => typeFilter === 'ALL' || (p.partner_type ?? 'INSTALLER') === typeFilter)
    .filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.city || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const rate = (p: Installer) => {
    const v = p.partner_type === 'EPC' ? p.price_ppw : p.cost_ppw;
    return v != null ? `$${Number(v).toFixed(2)}/W` : '—';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div role="group" aria-label="Filter by company type" className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              aria-pressed={typeFilter === chip.value}
              onClick={() => setTypeFilter(chip.value)}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                typeFilter === chip.value
                  ? 'border-primary bg-primary text-primary-foreground font-medium'
                  : 'border-border bg-card text-foreground hover:bg-muted'
              )}
            >
              {chip.label} <span className="opacity-75">{chip.count}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search partners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48"
          />
          {/* Hidden button that can be triggered from parent */}
          <Button
            onClick={() => setIsAddingInstaller(true)}
            disabled={isLoading}
            className="hidden"
            data-add-installer-button
          >
            <HardHat className="mr-2 h-4 w-4" />
            New partner
          </Button>
        </div>
      </div>

      <div className="overflow-hidden border rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="font-semibold text-muted-foreground">Company</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Type</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Rate</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Location</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Contact</TableHead>
                <TableHead className="font-semibold text-muted-foreground">People</TableHead>
                <TableHead className="text-right font-semibold text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-card">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading partners...
                  </TableCell>
                </TableRow>
              ) : filteredInstallers.length > 0 ? (
                filteredInstallers.map((installer) => (
                  <TableRow key={installer.id} className="hover:bg-muted">
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="text-sm text-foreground">{installer.name}</span>
                        {!installer.is_active && <span className="text-xs text-destructive">Inactive</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          PARTNER_BADGE_CLASS[installer.partner_type === 'EPC' ? 'EPC' : 'INSTALLER']
                        )}
                      >
                        {partnerTypeLabel(installer.partner_type)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-foreground">{rate(installer)}</TableCell>
                    <TableCell className="text-sm text-foreground">
                      {installer.city || installer.state
                        ? [installer.city, installer.state].filter(Boolean).join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-foreground">
                        {installer.email || installer.phone ? (
                          <>
                            {installer.email && <div>{installer.email}</div>}
                            {installer.phone && <div className="text-xs text-muted-foreground">{installer.phone}</div>}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-foreground">{installer.people_count ?? 0}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${installer.name}`}
                        className="text-muted-foreground hover:text-primary hover:bg-muted"
                        onClick={() => setEditingInstaller(installer)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${installer.name}`}
                        className="ml-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeletingInstaller(installer)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchQuery || typeFilter !== 'ALL' ? 'No partners match.' : 'No partner companies yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <SlidePanel
        isOpen={isAddingInstaller}
        onClose={() => setIsAddingInstaller(false)}
        title="New partner"
        width="2xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="text-sm text-muted-foreground mb-6">
          Enter the details for the new partner company.
        </div>
        <InstallerForm
          initialData={null}
          defaultType={typeFilter === 'EPC' ? 'EPC' : 'INSTALLER'}
          onSubmit={handleAddInstaller}
          onCancel={() => setIsAddingInstaller(false)}
          isSubmitting={isLoading}
          managers={managers}
          isEditing={false}
        />
      </SlidePanel>

      <SlidePanel
        isOpen={!!editingInstaller}
        onClose={() => setEditingInstaller(null)}
        title="Edit partner"
        width="2xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="text-sm text-muted-foreground mb-6">
          Update the partner company details.
        </div>
        <InstallerForm
          initialData={editingInstaller}
          onSubmit={handleUpdateInstaller}
          onCancel={() => setEditingInstaller(null)}
          isSubmitting={isLoading}
          managers={managers}
          isEditing={true}
        />
      </SlidePanel>

      <DeleteInstallerDialog
        open={!!deletingInstaller}
        onOpenChange={(open) => !open && setDeletingInstaller(null)}
        onConfirm={handleDeleteInstaller}
        installerName={deletingInstaller?.name || ''}
      />
    </div>
  );
}